import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { dateTime, humanize, money, newIdempotencyKey } from '@/lib/utils';
import { DataTable, Dialog, FilterField, SearchFilterBar } from '@/components/ui/layout';
import { Alert, Badge, Button, Field, Select, Spinner, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { Pager, StockStatusBadge, invalidateStockControl, signedMoney, signedQty } from './stock-control-common';
import {
  ADJUSTMENT_STATUSES,
  PAGE_SIZE,
  type AdjustmentDecision,
  type AdjustmentDetail,
  type AdjustmentRow,
} from './stock-control-types';

const DECISION_COPY: Record<AdjustmentDecision, { title: string; confirm: string; help: string; done: string }> = {
  approve: {
    title: 'Approve this request',
    confirm: 'Approve',
    help: 'The stock moves and the ledger posts as soon as you approve.',
    done: 'Approved. The stock has been adjusted.',
  },
  reject: {
    title: 'Reject this request',
    confirm: 'Reject',
    help: 'Nothing moves. The person who raised it sees your note.',
    done: 'Request rejected. No stock moved.',
  },
  withdraw: {
    title: 'Withdraw this request',
    confirm: 'Withdraw',
    help: 'The request is closed without any stock moving.',
    done: 'Request withdrawn.',
  },
};

const DECISION_PATH: Record<AdjustmentDecision, string> = {
  approve: 'approve',
  reject: 'reject',
  withdraw: 'cancel',
};

export function AdjustmentsPanel() {
  const { can, user } = useAuth();
  const [status, setStatus] = useState('pending');
  const [offset, setOffset] = useState(0);
  const [openId, setOpenId] = useState<string | null>(null);
  const [deciding, setDeciding] = useState<{ request: AdjustmentRow; action: AdjustmentDecision } | null>(null);

  const requests = useQuery({
    queryKey: ['stock-control-adjustments', status, offset],
    queryFn: () =>
      get<AdjustmentRow[]>(`/pharmacy/stock/adjustments${qs({ status: status || undefined, limit: PAGE_SIZE, offset })}`),
    placeholderData: keepPreviousData,
  });

  const mayApprove = can('stock.adjust.approve');
  const mayWithdraw = (row: AdjustmentRow) => mayApprove || (row.requestedBy !== null && row.requestedBy === user?.id);
  const rowCount = requests.data?.length ?? 0;
  const paged = offset > 0 || rowCount >= PAGE_SIZE;

  return (
    <>
      <SearchFilterBar
        filters={
          <FilterField label={tr('pharmacy', 'status')}>
            <Select
              className="w-48"
              aria-label={tr('pharmacy', 'filter_by_status')}
              value={status}
              onChange={(event) => {
                setStatus(event.target.value);
                setOffset(0);
              }}
            >
              <option value="">{tr('pharmacy', 'all_requests')}</option>
              {ADJUSTMENT_STATUSES.map((option) => (
                <option key={option} value={option}>
                  {humanize(option)}
                </option>
              ))}
            </Select>
          </FilterField>
        }
        onReset={
          status !== 'pending'
            ? () => {
                setStatus('pending');
                setOffset(0);
              }
            : undefined
        }
      />

      <DataTable
        rows={requests.data}
        loading={requests.isLoading} refreshing={requests.isFetching}
        error={requests.error}
        rowKey={(row) => row.id}
        onRowClick={(row) => setOpenId(row.id)}
        empty={
          status === 'pending'
            ? tr('pharmacy', 'nothing_is_waiting_for_approval_adjustments')
            : tr('pharmacy', 'no_requests_with_that_status')
        }
        footer={
          paged ? (
            <div className="w-full">
              <Pager offset={offset} pageSize={PAGE_SIZE} rowCount={rowCount} disabled={requests.isFetching} onOffsetChange={setOffset} />
            </div>
          ) : undefined
        }
        columns={[
          { header: tr('pharmacy', 'number'), sort: (row) => row.number, cell: (row) => <span className="font-mono text-xs">{row.number}</span> },
          {
            header: tr('pharmacy', 'type'),
            sort: (row) => row.type,
            cell: (row) => (
              <div className="flex flex-wrap items-center gap-1">
                <span>{humanize(row.type)}</span>
                {row.source === 'stock_take' && <Badge variant="secondary">{tr('pharmacy', 'stock_take')}</Badge>}
              </div>
            ),
          },
          { header: tr('pharmacy', 'store'), sort: (row) => row.store.name, cell: (row) => row.store.name },
          { header: tr('pharmacy', 'lines'), align: 'right', sort: (row) => row.lineCount, cell: (row) => row.lineCount },
          { header: tr('pharmacy', 'value'), align: 'right', sort: (row) => row.valueMinor, cell: (row) => money(row.valueMinor) },
          {
            header: tr('pharmacy', 'net_at_cost'),
            align: 'right',
            sort: (row) => row.netValueMinor,
            cell: (row) => (
              <span className={row.netValueMinor < 0 ? 'font-medium text-destructive' : ''}>{signedMoney(row.netValueMinor)}</span>
            ),
          },
          {
            header: tr('pharmacy', 'requested'),
            sort: (row) => row.requestedAt,
            cell: (row) => (
              <div>
                <p>{row.requestedByName ?? '—'}</p>
                <p className="text-xs text-muted-foreground">{dateTime(row.requestedAt)}</p>
              </div>
            ),
          },
          {
            header: tr('pharmacy', 'status'),
            sort: (row) => row.status,
            cell: (row) => (
              <div>
                <StockStatusBadge status={row.status} />
                {row.decidedByName && <p className="mt-0.5 text-xs text-muted-foreground">{tr('pharmacy', 'by')} {row.decidedByName}</p>}
              </div>
            ),
          },
          {
            header: '',
            align: 'right',
            cell: (row) =>
              row.status === 'pending' ? (
                <div className="flex justify-end gap-1">
                  {mayApprove && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(event) => {
                          event.stopPropagation();
                          setDeciding({ request: row, action: 'approve' });
                        }}
                      >
                        {tr('pharmacy', 'approve')}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(event) => {
                          event.stopPropagation();
                          setDeciding({ request: row, action: 'reject' });
                        }}
                      >
                        {tr('pharmacy', 'reject')}
                      </Button>
                    </>
                  )}
                  {mayWithdraw(row) && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(event) => {
                        event.stopPropagation();
                        setDeciding({ request: row, action: 'withdraw' });
                      }}
                    >
                      {tr('pharmacy', 'withdraw')}
                    </Button>
                  )}
                </div>
              ) : null,
          },
        ]}
      />

      {openId && (
        <AdjustmentDetailDialog
          requestId={openId}
          mayApprove={mayApprove}
          mayWithdraw={mayWithdraw}
          onClose={() => setOpenId(null)}
          onDecide={(request, action) => {
            setOpenId(null);
            setDeciding({ request, action });
          }}
        />
      )}
      {deciding && (
        <DecideAdjustmentDialog request={deciding.request} action={deciding.action} onClose={() => setDeciding(null)} />
      )}
    </>
  );
}

function AdjustmentDetailDialog({
  requestId,
  mayApprove,
  mayWithdraw,
  onClose,
  onDecide,
}: {
  requestId: string;
  mayApprove: boolean;
  mayWithdraw: (row: AdjustmentRow) => boolean;
  onClose: () => void;
  onDecide: (request: AdjustmentRow, action: AdjustmentDecision) => void;
}) {
  const detail = useQuery({
    queryKey: ['stock-control-adjustment', requestId],
    queryFn: () => get<AdjustmentDetail>(`/pharmacy/stock/adjustments/${requestId}`),
  });
  const request = detail.data;

  return (
    <Dialog
      open
      wide
      onClose={onClose}
      title={request ? `${request.number} · ${humanize(request.type)}` : tr('pharmacy', 'adjustment_request')}
      description={request ? tr('pharmacy', 'x_raised_x', { name: request.store.name, requestedAt: dateTime(request.requestedAt) }) : undefined}
      footer={
        <>
          {request?.status === 'pending' && mayApprove && (
            <>
              <Button variant="outline" onClick={() => onDecide(request, 'approve')}>
                {tr('pharmacy', 'approve')}
              </Button>
              <Button variant="ghost" onClick={() => onDecide(request, 'reject')}>
                {tr('pharmacy', 'reject')}
              </Button>
            </>
          )}
          {request?.status === 'pending' && mayWithdraw(request) && (
            <Button variant="ghost" onClick={() => onDecide(request, 'withdraw')}>
              {tr('pharmacy', 'withdraw')}
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            {tr('pharmacy', 'close')}
          </Button>
        </>
      }
    >
      {detail.isLoading && (
        <div className="flex items-center justify-center gap-2 p-8 text-sm text-muted-foreground">
          <Spinner /> {tr('pharmacy', 'loading')}
        </div>
      )}
      {detail.error && <Alert variant="destructive">{describeError(detail.error)}</Alert>}
      {request && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <StockStatusBadge status={request.status} />
            {request.source === 'stock_take' && <Badge variant="secondary">{tr('pharmacy', 'raised_by_a_stock_take')}</Badge>}
            <span className="text-muted-foreground">
              {tr('pharmacy', 'requested_by')} {request.requestedByName ?? tr('pharmacy', 'unknown')} {tr('pharmacy', 'value_2')} {money(request.valueMinor)} {tr('pharmacy', 'net')}{' '}
              {signedMoney(request.netValueMinor)} {tr('pharmacy', 'at_cost')}
            </span>
          </div>
          {request.note && <p className="text-sm">{request.note}</p>}
          {request.decidedAt && (
            <p className="text-sm text-muted-foreground">
              {humanize(request.status)} {tr('pharmacy', 'by')} {request.decidedByName ?? tr('pharmacy', 'unknown')} {tr('pharmacy', 'on')} {dateTime(request.decidedAt)}
              {request.decisionNote ? ` — ${request.decisionNote}` : ''}
            </p>
          )}
          <DataTable
            dense
            rows={request.lines}
            rowKey={(line) => line.stockBatchId}
            empty={tr('pharmacy', 'this_request_has_no_lines')}
            columns={[
              { header: tr('pharmacy', 'product'), cell: (line) => line.productName },
              { header: tr('pharmacy', 'batch'), cell: (line) => <span className="font-mono text-xs">{line.batchNo}</span> },
              {
                header: tr('pharmacy', 'change'),
                align: 'right',
                cell: (line) => (
                  <span className={line.qtyDelta < 0 ? 'font-medium text-destructive' : 'font-medium text-success'}>
                    {signedQty(line.qtyDelta)}
                  </span>
                ),
              },
              { header: tr('pharmacy', 'unit_cost'), align: 'right', cell: (line) => money(line.unitCostMinor) },
              {
                header: tr('pharmacy', 'value'),
                align: 'right',
                cell: (line) => signedMoney(Math.round(line.qtyDelta * line.unitCostMinor)),
              },
              { header: tr('pharmacy', 'reason'), cell: (line) => line.reason },
              {
                header: tr('pharmacy', 'loss_category'),
                cell: (line) => (line.lossCategory ? <Badge variant="secondary">{humanize(line.lossCategory, 'loss_category')}</Badge> : '—'),
              },
            ]}
          />
          <p className="text-xs text-muted-foreground">
            {request.lineCount} {tr('pharmacy', 'line')}{request.lineCount === 1 ? '' : 's'}{tr('pharmacy', 'values_are_quantity_times_the_batch_cost_when')}
          </p>
        </div>
      )}
    </Dialog>
  );
}

function DecideAdjustmentDialog({
  request,
  action,
  onClose,
}: {
  request: AdjustmentRow;
  action: AdjustmentDecision;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [idempotencyKey] = useState(() => newIdempotencyKey());
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const copy = DECISION_COPY[action];

  const decide = useMutation({
    mutationFn: () =>
      post(
        `/pharmacy/stock/adjustments/${request.id}/${DECISION_PATH[action]}`,
        action === 'withdraw' ? undefined : { note: note.trim() || undefined },
        { idempotencyKey },
      ),
    onSuccess: () => {
      toast.success(`${request.number}: ${copy.done}`);
      void invalidateStockControl(queryClient);
      onClose();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={copy.title}
      description={`${request.number} · ${humanize(request.type)} · ${request.store.name} · ${money(request.valueMinor)}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={decide.isPending}>
            {tr('pharmacy', 'cancel')}
          </Button>
          <Button
            variant={action === 'approve' ? 'default' : 'destructive'}
            onClick={() => {
              setError(null);
              decide.mutate();
            }}
            loading={decide.isPending}
          >
            {copy.confirm}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">{copy.help}</p>
        {action !== 'withdraw' && (
          <Field label={tr('pharmacy', 'note')} hint={tr('pharmacy', 'optional')}>
            <Textarea rows={2} maxLength={300} value={note} onChange={(event) => setNote(event.target.value)} />
          </Field>
        )}
        {error && <Alert variant="destructive">{error}</Alert>}
      </div>
    </Dialog>
  );
}
